import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SystemHealthCard from '../../components/SystemHealthCard';
import {
  ArrowLeft, Loader2, Activity, Database, Mail, MessageSquare, Plug,
  RefreshCw, AlertTriangle, CheckCircle2, ExternalLink, Cog,
} from 'lucide-react';

/**
 * Iter-224g — Dedicated System Health page.
 *
 * Moves background-diagnostics surface off the main /admin/home page so
 * operations / staffing / payroll / invoicing / closeout content can
 * lead the dashboard. This page is opt-in: admins reach it via the
 * "View Full Diagnostics →" button at the bottom of /admin/home.
 *
 * What lives here:
 *   • The existing SystemHealthCard (now expanded by default since this
 *     IS the dedicated diagnostics page).
 *   • Integration status hub link.
 *   • Database / migrations snapshot (best-effort).
 *   • Live ops health widget (mirrors the operational scoreboard).
 */
const AdminSystemHealth = () => {
  const navigate = useNavigate();
  const [integrationsHealth, setIntegrationsHealth] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [intRes, migRes] = await Promise.allSettled([
        api.get('/admin/integrations/health'),
        api.get('/admin/system/migrations-status'),
      ]);
      if (intRes.status === 'fulfilled') setIntegrationsHealth(intRes.value.data);
      if (migRes.status === 'fulfilled') setMigrationStatus(migRes.value.data);
    } finally {
      setIntegrationsLoading(false);
      setMigrationLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Always-expand the SystemHealthCard on this dedicated page.
  useEffect(() => {
    try { localStorage.setItem('ievntur_system_health_expanded', '1'); }
    catch { /* noop */ }
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-system-health-page">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} data-testid="back-to-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Activity className="w-5 h-5 text-cyan-600 shrink-0" />
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate">
              System Health · Diagnostics
            </h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            disabled={refreshing}
            className="border-slate-300"
            data-testid="refresh-system-health-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <p className="text-sm text-slate-600">
          Background diagnostics. Operational data (staffing, payouts, invoicing,
          closeout) lives on the main <button
            type="button"
            onClick={() => navigate('/admin/home')}
            className="underline text-cyan-700 hover:text-cyan-900"
          >dashboard</button>.
        </p>

        {/* Communications + Payments + Compliance snapshot */}
        <section data-testid="section-comms-payments-compliance">
          <SystemHealthCard />
        </section>

        {/* Integrations */}
        <section data-testid="section-integrations">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-900">
                <Plug className="w-4 h-4 text-cyan-600" />
                External Integrations
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/integrations')}
                className="text-xs h-7"
                data-testid="open-integrations-page-btn"
              >
                Manage <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              {integrationsLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading integration status…
                </div>
              ) : !integrationsHealth ? (
                <p className="text-xs text-slate-500 py-2">
                  Integration health endpoint unavailable. Open the integrations
                  page directly to verify Xero, Yardstik, Stripe, Twilio and
                  SendGrid status.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {(integrationsHealth.integrations || []).map((it) => (
                    <li
                      key={it.id || it.name}
                      className="flex items-center justify-between py-2"
                      data-testid={`integration-row-${it.id || it.name}`}
                    >
                      <span className="font-medium text-slate-800">{it.name}</span>
                      <Badge
                        className={`text-[10px] ${
                          it.status === 'healthy' || it.status === 'connected'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : it.status === 'degraded'
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {it.status === 'healthy' || it.status === 'connected' ? (
                          <CheckCircle2 className="w-3 h-3 mr-0.5" />
                        ) : (
                          <AlertTriangle className="w-3 h-3 mr-0.5" />
                        )}
                        {it.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Migrations / database */}
        <section data-testid="section-migrations">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-900">
                <Database className="w-4 h-4 text-cyan-600" />
                Database · Migrations
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {migrationLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading migration status…
                </div>
              ) : !migrationStatus ? (
                <p className="text-xs text-slate-500 py-2">
                  Migration status endpoint not implemented yet. Check
                  <code className="px-1 mx-1 bg-slate-100 rounded text-slate-700">backend/migrations/</code>
                  and <code className="px-1 mx-1 bg-slate-100 rounded text-slate-700">schema_migrations</code> table for current state.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Latest applied</p>
                    <p className="text-base font-semibold text-slate-900 mt-0.5" data-testid="latest-migration">
                      {migrationStatus.latest_version}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Pending</p>
                    <p className="text-base font-semibold text-slate-900 mt-0.5" data-testid="pending-migration-count">
                      {migrationStatus.pending_count ?? 0}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Quick links */}
        <section data-testid="section-quick-links">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-900">
                <Cog className="w-4 h-4 text-cyan-600" />
                Quick Diagnostic Links
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="justify-start text-sm h-auto py-2"
                  onClick={() => navigate('/admin/integrations')}
                  data-testid="link-integrations"
                >
                  <Plug className="w-4 h-4 mr-2 text-cyan-600" />
                  Integrations
                </Button>
                <Button
                  variant="outline"
                  className="justify-start text-sm h-auto py-2"
                  onClick={() => navigate('/admin/operations')}
                  data-testid="link-operations"
                >
                  <Activity className="w-4 h-4 mr-2 text-cyan-600" />
                  Operations Center
                </Button>
                <Button
                  variant="outline"
                  className="justify-start text-sm h-auto py-2"
                  onClick={() => navigate('/admin/messaging-audit')}
                  data-testid="link-messaging-audit"
                >
                  <MessageSquare className="w-4 h-4 mr-2 text-cyan-600" />
                  Messaging Audit
                </Button>
                <Button
                  variant="outline"
                  className="justify-start text-sm h-auto py-2"
                  onClick={() => navigate('/admin/email-logs')}
                  data-testid="link-email-logs"
                >
                  <Mail className="w-4 h-4 mr-2 text-cyan-600" />
                  Email Logs
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default AdminSystemHealth;
